import { useTranslations } from '@vocab/react';
import { Heading, Stack, Text } from 'braid-design-system';
import type { ReactNode } from 'react';

import type { ProfileView } from '../../../types/profile.types';
import { parseBulletPoints } from '../../../utils/parseBulletPoints';
import { CareerHistory } from '../CareerHistory/CareerHistory';
import { Categories } from '../Categories/Categories';
import { CurrentStatus } from '../CurrentStatus/CurrentStatus';
import { Education } from '../Education/Education';
import { Licences } from '../Licences/Licences';

import translations from './.vocab';

interface Props {
  profile: ProfileView;
  renderDisclaimer?: () => ReactNode;
}

export const ProfilePanel = ({ profile, renderDisclaimer }: Props) => {
  const { t } = useTranslations(translations);

  const {
    personalSummary,
    workHistories,
    education,
    licences,
    skills,
    languages,
    rightsToWork,
    currentIndustry,
    availability,
    workTypes,
    locationInProfile,
    locationInCV,
  } = profile;

  const hasPersonalSummary =
    typeof personalSummary === 'string' && personalSummary.trim().length > 0;
  const hasWorkHistories = workHistories.length > 0;
  const hasEducation = education && education.length > 0;
  const hasLicences = licences && licences.length > 0;
  const hasSkills = skills && skills.length > 0;
  const hasLanguages = languages && languages.length > 0;
  const hasRightsToWork = rightsToWork && rightsToWork.length > 0;
  const hasCurrentStatus = Boolean(
    currentIndustry ||
    availability ||
    workTypes ||
    locationInProfile ||
    locationInCV ||
    hasRightsToWork,
  );

  return (
    <Stack space="xlarge">
      {hasPersonalSummary && (
        <Stack space="large">
          <Heading level="4">{t('PERSONAL_SUMMARY')}</Heading>
          <Text>
            <span style={{ whiteSpace: 'pre-wrap' }}>
              {parseBulletPoints(personalSummary)}
            </span>
          </Text>
        </Stack>
      )}

      {hasWorkHistories && (
        <Stack space="large">
          <Heading level="4">{t('CAREER_HISTORY')}</Heading>
          <CareerHistory workHistories={workHistories} />
        </Stack>
      )}

      {hasEducation && (
        <Stack space="large">
          <Heading level="4">{t('Education')}</Heading>
          <Education profileEducation={education} />
        </Stack>
      )}

      {hasLicences && (
        <Stack space="large">
          <Heading level="4">{t('LICENCES_AND_CERTIFICATIONS')}</Heading>
          <Licences licences={licences} />
        </Stack>
      )}

      {hasSkills && (
        <Stack space="large">
          <Heading level="4">{t('SKILLS')}</Heading>
          <Categories categories={skills} />
        </Stack>
      )}

      {hasLanguages && (
        <Stack space="large">
          <Heading level="4">{t('LANGUAGES')}</Heading>
          <Categories categories={languages} />
        </Stack>
      )}

      {hasCurrentStatus && (
        <Stack space="large">
          <Heading level="4">{t('CURRENT_STATUS')}</Heading>
          <CurrentStatus
            rightsToWork={rightsToWork || []}
            currentIndustry={currentIndustry}
            availability={availability}
            workTypes={workTypes}
            locationInProfile={locationInProfile}
            locationInCV={locationInCV}
          />
        </Stack>
      )}
      {profile.hasVerifiedCredentials && renderDisclaimer ? (
        <>{renderDisclaimer()}</>
      ) : null}
    </Stack>
  );
};
